import { actionInterceptor } from '../middleware/actionsInterceptorMiddleware';
import { AccountModel, UserMainAccountModel } from '@shared/models';
import { siteReducer } from '../entity/sites/SitesReducer';
import {
  BaseAccountStoreModel,
  accountsStoreName,
  handleFulfilledAction,
  handleMultipleLoadingActions,
} from '@hwm/ui-lib';
import { PayloadAction, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { accountsThunks } from './accounts.thunks';
import { userProfileThunks } from './user-profile.thunks';

export interface AccountStoreState extends BaseAccountStoreModel<AccountModel, UserMainAccountModel> {}

export const accountsAdapter = createEntityAdapter({
  selectId: (acc: AccountModel) => acc.id || acc['accountId'],
  sortComparer: (a, b) => a.name.localeCompare(b.name),
});

const initialState: AccountStoreState = {
  error: null,
  hierarchy: [],
  loadingPending: 0,
  selectedAccount: null,
  subaccounts: accountsAdapter.getInitialState(),
  userRole: '',
  userMainAccount: null,
  includeArchived: false,
};

const slice = createSlice({
  name: accountsStoreName,
  initialState,
  reducers: {
    setSelectedAccount(state, action: PayloadAction<{ account: AccountModel }>) {
      state.selectedAccount = action.payload.account;
    },
    setHierarchy(state, action: PayloadAction<{ hierarchy: AccountModel[] }>) {
      state.hierarchy = action.payload.hierarchy;
    },
    accountsReceived(state, action: PayloadAction<{ accounts: AccountModel[] }>) {
      accountsAdapter.setAll(state.subaccounts, action.payload.accounts);
    },
    //
    setUserMainAccount: (state, { payload }: { payload: UserMainAccountModel }) => {
      state.userMainAccount = payload;
    },
    setUserRole: (state, { payload }: { payload: string }) => {
      state.userRole = payload;
    },
    setIncludeArchived: (state, { payload }: { payload: boolean }) => {
      state.includeArchived = payload;
    },
  },
  extraReducers: (builder) => {
    handleMultipleLoadingActions([accountsThunks.fetchSubAccounts, accountsThunks.fetchById], builder);

    handleFulfilledAction(accountsThunks.fetchSubAccounts, builder, (state, payload) => {
      accountsAdapter.setAll(state.subaccounts, payload);
    });
    handleFulfilledAction(accountsThunks.fetchById, builder, (state, payload) => {
      state.selectedAccount = payload;
      state.hierarchy = [...(payload?.ancestors ?? []), payload];
    });
  },
});

export const accountsReducer = {
  slice,
};

actionInterceptor.addActionsCallback<typeof userProfileThunks.getUserProfile.fulfilled>({
  actionTypes: [userProfileThunks.getUserProfile.fulfilled.type],
  callback: ({ dispatch, action }) => {
    dispatch(slice.actions.setUserMainAccount(action.payload ?? null));

    const accountId = new URLSearchParams(window.location.search).get('accountId');

    if (accountId) {
      dispatch(accountsThunks.fetchById({ accountId }));
    } else {
      dispatch(
        slice.actions.setSelectedAccount({
          account: action.payload,
        })
      );
      dispatch(
        slice.actions.setHierarchy({
          hierarchy: [action.payload],
        })
      );
    }
  },
});

actionInterceptor.addActionsCallback<typeof accountsThunks.fetchById.fulfilled>({
  actionTypes: [accountsThunks.fetchById.fulfilled.type],
  callback: ({ dispatch, action }) => {
    const accId = action.payload?.['id'] ?? action.payload?.['accountId'];

    dispatch(
      accountsThunks.fetchSubAccounts({
        accountId: accId.toString(),
      })
    );
  },
});

actionInterceptor.addActionsCallback<typeof accountsThunks.fetchById.fulfilled>({
  actionTypes: [accountsThunks.fetchById.fulfilled.type],
  callback: ({ dispatch }) => {
    dispatch(siteReducer.slice.actions.setAllAndSelectedEmpty());
    dispatch(siteReducer.slice.actions.setUserSelectedEmpty());
  },
});

actionInterceptor.addActionsCallback<typeof slice.actions.setSelectedAccount>({
  actionTypes: [slice.actions.setSelectedAccount.type],
  callback: ({ dispatch }) => {
    dispatch(siteReducer.slice.actions.setAllAndSelectedEmpty());
    dispatch(siteReducer.slice.actions.setUserSelectedEmpty());
  },
});
